import { type ReactNode, createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { setCalendarView as setCalendarViewPreference } from "@/lib/dexie/preferences";
import type { CalendarView } from "@/components/calendar/event-calendar/types";
import { db } from "@/lib/dexie/dexieDB";
import { PREFERENCE_KEYS } from "@/lib/dexie/preferences";

interface PreferencesContextType {
  calendarView: CalendarView;
  setCalendarView: (view: CalendarView) => Promise<void>;
  isLoading: boolean;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return context;
}

export function useCalendarView() {
  const { calendarView, setCalendarView } = usePreferences();
  return { calendarView, setCalendarView };
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [calendarView, setCalendarViewState] = useState<CalendarView>("week");
  const [isLoading, setIsLoading] = useState(true);

  // Use Dexie live query to watch for changes
  const dexieCalendarView = useLiveQuery(
    () => db.preferences.get(PREFERENCE_KEYS.CALENDAR_VIEW),
    [],
  );

  // Update state when Dexie value changes
  useEffect(() => {
    if (dexieCalendarView !== undefined) {
      const view = (dexieCalendarView?.value as CalendarView) || "week";
      setCalendarViewState(view);
      setIsLoading(false);
    } else if (dexieCalendarView === null) {
      // No preference stored yet, use default
      setCalendarViewState("week");
      setIsLoading(false);
    }
  }, [dexieCalendarView]);

  // Set calendar view function
  const setCalendarView = useCallback(async (view: CalendarView) => {
    // Optimistically update the state for immediate UI response
    setCalendarViewState(view);
    // Then persist to storage
    await setCalendarViewPreference(view);
  }, []);

  const value = {
    calendarView,
    setCalendarView,
    isLoading,
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}
